import React, { useMemo, useCallback, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { Search, Sparkles, X, CheckCircle2, Lock } from 'lucide-react';
import { EQUIPMENT_SLOT_LAYOUT, EQUIPMENT_SLOT_GROUPS, RETIRED_EQUIPMENT_SLOT_KEYS } from './equipmentSlots';
import { getCopyConflict, getHandConflict, isTwoHandedWeapon } from '../../../shared/rules/equipmentHands.mjs';
import { attunedEquipment, getAttunementConflict, USE_MAGIC_DEVICE_ATTUNEMENT_LIMIT } from '../../../shared/rules/equipmentSlots.mjs';
import { hasSubclassFeature } from '../../../shared/rules/subclasses/index.mjs';
import { calculateCharacterArmorClass } from '../utils/characterMetrics';
import ItemIcon from '../../common/ItemIcon';
import styles from './EquipmentRack.module.scss';

const FLAT_SLOTS = EQUIPMENT_SLOT_LAYOUT.flat();
const DEFAULT_ALLOWED_SOURCES = ['weapon', 'armor', 'item', 'accessory'];
const SOURCE_DESCRIPTIONS = { weapon: 'Weapon', armor: 'Armor', item: 'Item', accessory: 'Accessory' };
const DESCRIPTOR_KEYS = ['category', 'categories', 'type', 'types', 'slot', 'slots', 'tags', 'subtype', 'subType', 'equipmentSlot', 'equipmentSlots', 'targetSlot', 'targetSlots'];
const SLOT_METADATA_KEYS = ['slot', 'equipmentSlot', 'slots', 'equipmentSlots', 'targetSlot', 'targetSlots'];
const STAT_LABELS = { str: 'STR', dex: 'DEX', con: 'CON', int: 'INT', wis: 'WIS', cha: 'CHA' };
const RARITIES = ['all', 'common', 'uncommon', 'rare', 'epic', 'legendary', 'artifact'];
const toLowercaseStrings = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter((entry) => typeof entry === 'string' && entry.trim().length).map((entry) => entry.toLowerCase());
  if (typeof value === 'string') return value.trim().length ? [value.toLowerCase()] : [];
  return [];
};
const normalizeSlotEntries = (value) => {
  const normalized = new Set();
  toLowercaseStrings(value).forEach((entry) => {
    normalized.add(entry);
    const compact = entry.replace(/[\s_-]+/g, '');
    if (compact) normalized.add(compact);
  });
  return Array.from(normalized);
};
const getItemDescriptors = (item) => {
  if (!item || typeof item !== 'object') return [];
  const descriptors = new Set();
  DESCRIPTOR_KEYS.forEach((key) => toLowercaseStrings(item[key]).forEach((descriptor) => descriptors.add(descriptor)));
  return Array.from(descriptors);
};
const matchesAllowedValues = (descriptors, allowedValues = []) => {
  if (!allowedValues?.length || !descriptors?.length) return true;
  return allowedValues.some((allowed) => descriptors.some((descriptor) => descriptor.includes(allowed.toLowerCase())));
};
const getMetadataFilterEntries = (filters) => {
  if (!filters || typeof filters !== 'object') return [];
  const entries = new Set();
  SLOT_METADATA_KEYS.forEach((key) => filters[key] && normalizeSlotEntries(filters[key]).forEach((entry) => entries.add(entry)));
  return Array.from(entries);
};
const getItemSlotMetadata = (item) => {
  const slots = new Set();
  if (!item || typeof item !== 'object') return slots;
  SLOT_METADATA_KEYS.forEach((key) => normalizeSlotEntries(item[key]).forEach((entry) => slots.add(entry)));
  return slots;
};
const matchesMetadataFilters = (item, filters) => {
  const metadataFilters = getMetadataFilterEntries(filters);
  if (!metadataFilters.length) return true;
  const itemSlots = getItemSlotMetadata(item);
  if (itemSlots.size === 0) return true;
  return metadataFilters.some((entry) => itemSlots.has(entry));
};
const slotMatchesItemMetadata = (slot, itemSlots) => {
  if (!slot || !itemSlots?.size) return true;
  return normalizeSlotEntries(slot.key || slot).some((entry) => itemSlots.has(entry));
};
const slotAllowsOption = (slot, option) => {
  if (!slot || !option) return false;
  const allowedSources = slot.allowedSources?.length ? slot.allowedSources : DEFAULT_ALLOWED_SOURCES;
  if (!allowedSources.includes(option.source)) return false;
  if (slot.excludeTwoHanded && option.source === 'weapon' && isTwoHandedWeapon(option.item)) return false;
  const sourceFilters = slot.filters?.[option.source];
  if (!sourceFilters) {
    if (option.source === 'armor' || option.source === 'accessory') {
      const itemSlots = getItemSlotMetadata(option.item);
      if (itemSlots.size > 0 && !slotMatchesItemMetadata(slot, itemSlots)) return false;
    }
    return true;
  }
  const descriptors = getItemDescriptors(option.item);
  if (sourceFilters.categories && !matchesAllowedValues(descriptors, sourceFilters.categories)) return false;
  if (sourceFilters.types && !matchesAllowedValues(descriptors, sourceFilters.types)) return false;
  return matchesMetadataFilters(option.item, sourceFilters);
};
const buildSlotOptions = (slot, optionsBySource) => {
  const allowedSources = slot?.allowedSources?.length ? slot.allowedSources : DEFAULT_ALLOWED_SOURCES;
  return allowedSources.flatMap((source) => (optionsBySource[source] || []).filter((option) => slotAllowsOption(slot, option)));
};
const getItemName = (item) => !item ? '' : typeof item === 'string' ? item : item.displayName || item.name || item.itemName || item.accessoryName || item.armorName || item.title || '';
const getItemSource = (item) => item && typeof item === 'object' ? item.source || item.__source || item.reference?.source : undefined;
const normalizeRarity = (item) => String(item?.rarity || item?.quality || 'common').toLowerCase();
const getDescription = (item) => item?.description || item?.notes || item?.detail || item?.flavor || 'No description recorded for this piece of gear.';
const getStatLines = (item) => {
  if (!item) return [];
  const lines = [];
  if (item.acBonus !== undefined && item.acBonus !== '') lines.push(`+${item.acBonus} AC`);
  if (item.attackBonus) lines.push(`+${item.attackBonus} Attack`);
  if (item.damage) lines.push(item.damage);
  Object.entries(item.statBonuses || {}).forEach(([key, value]) => Number(value) ? lines.push(`${Number(value) > 0 ? '+' : ''}${value} ${STAT_LABELS[key] || key.toUpperCase()}`) : null);
  Object.entries(item.statOverrides || {}).forEach(([key, value]) => value ? lines.push(`${STAT_LABELS[key] || key.toUpperCase()} set to ${value}`) : null);
  return lines;
};
const optionMatchesItem = (option, current) => {
  const itemName = getItemName(current);
  if (!itemName) return false;
  // Equipped copies remember which inventory entry they came from; older saves match by name.
  if (current.inventoryId) return option.inventoryId === current.inventoryId;
  const currentSource = getItemSource(current);
  const candidateName = getItemName(option.item);
  return currentSource ? option.source === currentSource && candidateName === itemName : candidateName === itemName;
};

export default function EquipmentRack({ equipment = {}, inventory = {}, onEquipmentChange, onSlotChange, disabled = false, character = {} }) {
  const [selectedSlotKey, setSelectedSlotKey] = useState('chest');
  const [search, setSearch] = useState('');
  const [rarityFilter, setRarityFilter] = useState('all');
  const [notice, setNotice] = useState('');
  const { weapons = [], armor = [], items = [], accessories = [] } = inventory || {};
  const slotLookup = useMemo(() => new Map(FLAT_SLOTS.map((slot) => [slot.key, slot])), []);
  const selectedSlot = slotLookup.get(selectedSlotKey) || slotLookup.get('chest');
  const equippedItems = useMemo(() => Object.values(equipment || {}).filter(Boolean), [equipment]);
  const inventoryOptions = useMemo(() => {
    const all = []; const bySource = {};
    const addOptions = (itemsList = [], source) => itemsList.forEach((item, index) => {
      const name = getItemName(item); if (!name) return;
      // Each owned copy gets its own id, so two Scimitars can fill both hands.
      const inventoryId = item?.inventoryId ?? item?.instanceId ?? `${source}:${index}`;
      const option = { value: `${source}-${index}-${name}`, label: name, item, source, inventoryId, description: SOURCE_DESCRIPTIONS[source] || 'Item' };
      all.push(option); (bySource[source] ||= []).push(option);
    });
    addOptions(weapons, 'weapon'); addOptions(armor, 'armor'); addOptions(items, 'item'); addOptions(accessories, 'accessory');
    return { all, bySource };
  }, [accessories, armor, items, weapons]);
  const optionMap = useMemo(() => new Map(inventoryOptions.all.map((opt) => [opt.value, opt])), [inventoryOptions]);
  const slotOptionsMap = useMemo(() => new Map(FLAT_SLOTS.map((slot) => [slot.key, buildSlotOptions(slot, inventoryOptions.bySource)])), [inventoryOptions]);
  const equippedSlotByInventoryId = useMemo(() => new Map(Object.entries(equipment || {}).filter(([, item]) => item?.inventoryId).map(([key, item]) => [item.inventoryId, key])), [equipment]);
  const mainHandItem = equipment?.mainHand;
  const offHandLocked = isTwoHandedWeapon(mainHandItem);
  const selectedOptions = slotOptionsMap.get(selectedSlot.key) || [];
  const currentItem = equipment?.[selectedSlot.key];
  const compatibleOptions = selectedOptions.filter((opt) => {
    const haystack = `${opt.label} ${opt.description} ${getStatLines(opt.item).join(' ')}`.toLowerCase();
    const matchesSearch = !search || haystack.includes(search.toLowerCase());
    const matchesRarity = rarityFilter === 'all' || normalizeRarity(opt.item) === rarityFilter;
    return matchesSearch && matchesRarity;
  });
  const summary = useMemo(() => {
    const stats = { attack: 0, weight: 0, passive: 0 };
    equippedItems.forEach((item) => {
      stats.attack += Number(item.attackBonus) || 0; stats.weight += Number(item.weight) || 0;
      stats.passive += Object.values(item.statBonuses || {}).filter(Boolean).length + Object.values(item.statOverrides || {}).filter(Boolean).length;
    });
    // Same AC the character sheet shows (armor formula, Dex cap, shield, unarmored defense).
    stats.ac = calculateCharacterArmorClass({ ...(character || {}), equipment });
    return stats;
  }, [character, equipment, equippedItems]);
  const retiredItems = RETIRED_EQUIPMENT_SLOT_KEYS.filter((key) => equipment?.[key]).map((key) => ({ slot: slotLookup.get(key), item: equipment[key] }));

  // Applies the hand and copy rules before saving; the server repeats the check.
  const commit = useCallback((proposed, keys) => {
    let next = proposed;
    let changedKeys = keys;
    let message = '';
    if (changedKeys.includes('mainHand') && isTwoHandedWeapon(next.mainHand) && next.offHand) {
      message = `${getItemName(next.mainHand)} needs both hands, so ${getItemName(next.offHand)} was unequipped.`;
      next = { ...next, offHand: null };
      changedKeys = [...changedKeys, 'offHand'];
    }
    const handsChanged = changedKeys.includes('mainHand') || changedKeys.includes('offHand');
    const attunementGrew = attunedEquipment(next).length > attunedEquipment(equipment).length;
    const conflict = (handsChanged && getHandConflict(next)) || getCopyConflict(next, { weapon: weapons, armor, item: items, accessories })
      || (attunementGrew && getAttunementConflict(next, hasSubclassFeature(character, 'rogue:thief:use-magic-device') ? USE_MAGIC_DEVICE_ATTUNEMENT_LIMIT : undefined));
    if (conflict) {
      setNotice(conflict);
      return;
    }
    setNotice(message);
    changedKeys.forEach((key) => onSlotChange?.(key, next[key] ?? null));
    onEquipmentChange?.(next);
  }, [accessories, armor, character, equipment, items, onEquipmentChange, onSlotChange, weapons]);

  const handleAssign = useCallback((slotKey, optionValue) => {
    if (typeof onEquipmentChange !== 'function' && typeof onSlotChange !== 'function') return;
    const option = optionValue ? optionMap.get(optionValue) : null;
    const nextItem = option ? { ...(typeof option.item === 'object' ? option.item : { name: getItemName(option.item) }), source: option.source, __source: option.source, inventoryId: option.inventoryId } : null;
    const next = { ...equipment, [slotKey]: nextItem };
    const changedKeys = [slotKey];
    // Picking a copy that is already equipped elsewhere moves it here.
    if (nextItem) {
      Object.entries(equipment || {}).forEach(([key, item]) => {
        if (key !== slotKey && item?.inventoryId && item.inventoryId === nextItem.inventoryId) {
          next[key] = null;
          changedKeys.push(key);
        }
      });
    }
    commit(next, changedKeys);
  }, [commit, equipment, onEquipmentChange, onSlotChange, optionMap]);

  const moveToMainHand = useCallback((fromKey) => {
    commit({ ...equipment, mainHand: equipment[fromKey], [fromKey]: null }, ['mainHand', fromKey]);
  }, [commit, equipment]);

  const renderSlot = (slot) => {
    const item = equipment?.[slot.key]; const rarity = normalizeRarity(item); const lines = getStatLines(item).slice(0, 2);
    const locked = slot.key === 'offHand' && offHandLocked && !item;
    const stateClass = item ? styles.equippedSocket : locked ? styles.lockedSocket : styles.emptySocket;
    const state = item ? ` equipped with ${getItemName(item)}` : locked ? `, used by ${getItemName(mainHandItem)}` : ', empty';
    return <button key={slot.key} type="button" className={`${styles.socket} ${stateClass} ${styles[`rarity_${rarity}`] || ''} ${selectedSlot.key === slot.key ? styles.selected : ''}`} onClick={() => setSelectedSlotKey(slot.key)} disabled={disabled} aria-label={`${slot.label} equipment slot${state}`}>
      <span className={styles.socketIcon}>{locked ? <Lock size={20} /> : <ItemIcon item={item} equipmentSlot={slot.key} size={34} title={slot.label} />}</span>
      <span className={styles.socketText}>
        <span className={styles.socketLabel}>{slot.label}</span>
        <span className={styles.socketName}>{item ? getItemName(item) : locked ? `Used by ${getItemName(mainHandItem)}` : 'Empty'}</span>
        {item && <span className={styles.socketMeta}>{isTwoHandedWeapon(item) ? 'Two-handed' : (lines[0] || SOURCE_DESCRIPTIONS[getItemSource(item)] || 'Equipped')}</span>}
      </span>
    </button>;
  };
  const detailItem = currentItem;
  const pickerLocked = selectedSlot.key === 'offHand' && offHandLocked && !currentItem;
  return <div className={styles.rackWrapper}>
    <div className={styles.bonusSummary} aria-label="Active equipment bonuses"><div><strong>{summary.ac ?? '—'}</strong><span>Armor Class</span></div><div><strong>{summary.attack >= 0 ? '+' : ''}{summary.attack}</strong><span>Attack Bonus</span></div><div><strong>{summary.passive}</strong><span>Passive Effects</span></div><div><strong>{summary.weight}</strong><span>Weight</span></div></div>
    {notice && <p className={styles.rackNotice} role="status">{notice}</p>}
    <div className={styles.equipmentShell}>
      <section className={styles.slotGroups} aria-label="Character equipment slots">
        {EQUIPMENT_SLOT_GROUPS.map((group) => <div key={group.key} className={`${styles.slotGroup} ${styles[`group_${group.key}`] || ''}`}>
          <h3 className={styles.groupLabel}>{group.label}</h3>
          <div className={styles.groupSlots}>{group.slots.map((key) => renderSlot(slotLookup.get(key)))}</div>
        </div>)}
        {retiredItems.length > 0 && <div className={styles.retiredNotice} role="note">
          <h3 className={styles.groupLabel}>Saved in retired slots</h3>
          <p>These slots aren't used in the 2024 rules. The gear stays saved until you move or unequip it.</p>
          {retiredItems.map(({ slot, item }) => <div key={slot.key} className={styles.retiredRow}>
            <span><strong>{getItemName(item)}</strong><small>{slot.label}</small></span>
            {slot.key === 'ranged' && !equipment?.mainHand && <Button size="sm" variant="outline-light" onClick={() => moveToMainHand(slot.key)} disabled={disabled}>Move to main hand</Button>}
            <Button size="sm" variant="outline-light" onClick={() => handleAssign(slot.key, '')} disabled={disabled} aria-label={`Unequip ${getItemName(item)} from ${slot.label}`}>Unequip</Button>
          </div>)}
        </div>}
      </section>
      <aside className={styles.detailPanel} aria-live="polite"><div className={styles.panelHeader}><span>{selectedSlot.label}</span><strong>{detailItem ? getItemName(detailItem) : pickerLocked ? `Used by ${getItemName(mainHandItem)}` : 'Empty Slot'}</strong></div>{detailItem ? <><div className={`${styles.itemHero} ${styles[`rarity_${normalizeRarity(detailItem)}`] || ''}`}><ItemIcon item={detailItem} equipmentSlot={selectedSlot.key} size={72}/><span>{normalizeRarity(detailItem)}</span></div><p>{getDescription(detailItem)}</p><div className={styles.statList}>{getStatLines(detailItem).map((line) => <span key={line}>{line}</span>)}</div><dl className={styles.itemFacts}><dt>Type</dt><dd>{SOURCE_DESCRIPTIONS[getItemSource(detailItem)] || 'Gear'}</dd><dt>Weight</dt><dd>{detailItem.weight || '—'}</dd><dt>Value</dt><dd>{detailItem.cost || detailItem.value || '—'}</dd></dl><Button variant="outline-light" className={styles.unequipButton} onClick={() => handleAssign(selectedSlot.key, '')} disabled={disabled}><X size={16}/> Unequip</Button></> : pickerLocked ? <div className={styles.emptyDetail}><Lock size={36}/><p>{getItemName(mainHandItem)} needs both hands. Unequip it from the main hand to use the off hand.</p></div> : <div className={styles.emptyDetail}><Sparkles size={42}/><p>Select compatible owned gear below to fill this socket.</p></div>}
        {!pickerLocked && <div className={styles.picker}><div className={styles.pickerTools}><label><Search size={15}/><Form.Control size="sm" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search compatible gear" aria-label="Search compatible gear" /></label><Form.Select size="sm" value={rarityFilter} onChange={(e) => setRarityFilter(e.target.value)} aria-label="Filter by rarity">{RARITIES.map((rarity) => <option key={rarity} value={rarity}>{rarity === 'all' ? 'All rarities' : rarity}</option>)}</Form.Select></div><div className={styles.itemList}>{compatibleOptions.length ? compatibleOptions.map((opt) => { const equipped = optionMatchesItem(opt, currentItem); const elsewhere = !equipped && equippedSlotByInventoryId.get(opt.inventoryId); const rarity = normalizeRarity(opt.item); return <button key={opt.value} type="button" className={`${styles.itemCard} ${styles[`rarity_${rarity}`] || ''}`} onClick={() => handleAssign(selectedSlot.key, opt.value)} disabled={disabled} title={`${opt.label} - ${getDescription(opt.item)}`}><ItemIcon item={opt.item} equipmentSlot={selectedSlot.key} size={34}/><span><strong>{opt.label}</strong><small>{rarity} • {getStatLines(opt.item).slice(0, 2).join(' • ') || opt.description}</small></span>{equipped ? <CheckCircle2 size={18}/> : <em>{elsewhere ? `Move from ${slotLookup.get(elsewhere)?.label || elsewhere}` : 'Equip'}</em>}</button>; }) : <p className={styles.noItems}>No owned compatible equipment found for this slot.</p>}</div></div>}
      </aside>
    </div>
  </div>;
}
